import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { BehaviorSubject, Observable } from 'rxjs';
import Fund from 'src/app/interfaces/fund';
import Organization from 'src/app/interfaces/organization';

@Component({
    selector: 'freeform-org-search',
    templateUrl: './freeform-org-search.component.html',
    styleUrls: ['./freeform-org-search.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FreeformOrgSearchComponent {
  @Input() value: string | null;
  @Input() placeholder: string = 'Purchaser Name';
  @Input() searchType: string = 'Organization';

  @Output() selectedOrg = new EventEmitter<Organization | Fund>();
  @Output() searchChanged = new EventEmitter<string | null>();

  @ViewChild('orgSearch') orgSearch: ElementRef;
  public timer: ReturnType<typeof setTimeout>;
  private _orgs: Organization[] | Fund[] = [];
  private orgs: BehaviorSubject<Organization[] | Fund[]> = new BehaviorSubject<
    Organization[]
  >(this._orgs);
  public orgs$: Observable<Organization[] | Fund[]> = this.orgs.asObservable();
  searching = false;
  searched = false;

  constructor(public apollo: Apollo) {}
  ngOnChanges(changes: SimpleChanges) {
    if (changes['searchType']) {
      let searchType = changes['searchType'];
      let element = this.orgSearch?.nativeElement;
      if (element && element.value && element.value.length) {
        switch (searchType.currentValue) {
          case 'Organization':
            this.queryOrgs(element.value);
            break;
          case 'Fund':
            this.queryFunds(element.value);
            break;
          default:
            this._orgs = [];
            this.orgs.next(this._orgs);
            break;
        }
      }
    }
  }
  queryFunds(query: string) {
    let sub = this.apollo
      .watchQuery({
        query: gql`
          query getFunds($name: String!, $search: String!) {
            search_funds(
              name: $name
              first: 10
              page: 1
              orderByInstr: [{ column: NAME, search: $search, order: ASC }]
            ) {
              data {
                id
                name
              }
              paginatorInfo {
                currentPage
                lastPage
              }
            }
          }
        `,
        variables: {
          name: '%' + query + '%',
          search: query,
        },
      })
      .valueChanges.subscribe(({ data, loading }) => {
        let anyData = data as {
          search_funds: {
            data: Fund[];
            paginatorInfo: {
              currentPage: number;
              lastPage: number;
            };
          };
        };
        this.searched = true;
        this.searching = false;
        this._orgs = anyData.search_funds.data;
        this.orgs.next(this._orgs);
        sub.unsubscribe();
      });
  }
  queryOrgs(query: string) {
    let sub = this.apollo
      .watchQuery({
        query: gql`
          query getOrgs($name: String!, $search: String!) {
            search_organizations(
              name: $name
              first: 10
              page: 1
              orderByInstr: [{ column: NAME, search: $search, order: ASC }]
            ) {
              data {
                id
                name
              }
              paginatorInfo {
                currentPage
                lastPage
              }
            }
          }
        `,
        variables: {
          name: '%' + query + '%',
          search: query,
        },
      })
      .valueChanges.subscribe(({ data, loading }) => {
        let anyData = data as {
          search_organizations: {
            data: Organization[];
            paginatorInfo: {
              currentPage: number;
              lastPage: number;
            };
          };
        };
        this.searched = true;
        this.searching = false;
        this._orgs = anyData.search_organizations.data;
        this.orgs.next(this._orgs);
        sub.unsubscribe();
      });
  }
  searchOrgs($event: Event) {
    let input = $event.target as HTMLInputElement;
    this.searched = false;
    if (this.timer) {
      clearTimeout(this.timer);
    }
    this.searchChanged.emit(input.value);
    if (input.value.length > 0) {
      this.timer = setTimeout(() => {
        this.searching = true;
        clearTimeout(this.timer);
        switch (this.searchType) {
          case 'Organization':
            this.queryOrgs(input.value);
            break;
          case 'Fund':
            this.queryFunds(input.value);
            break;
          default:
            break;
        }
      }, 300);
    } else {
      this.searchChanged.emit(null);
    }
  }
  selectOrg(org: Organization) {
    this.orgSearch?.nativeElement?.parentElement?.blur();
    this.selectedOrg.emit(org);
  }
}
